import {IonDatetime, ModalController} from '@ionic/angular';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {add, format, parse, parseISO} from 'date-fns';
import {es} from 'date-fns/locale';
import {AnimationsManagerService} from '../../../Services/Platform/Ionic/animations-manager.service';
import {PresentationTypeEnum} from '../../../Shared/Enum/presentation-type.enum';

@Component({
  selector: 'yu-datetime',
  templateUrl: './yu-datetime.component.html',
  styleUrls: ['./yu-datetime.component.scss'],
  standalone: false
})
export class YuDatetimeComponent {

  formattedDate?: string;
  internalValue!: string;

  @Input() label: string = '';
  @Input() placeholder: string = '';

  @Input() displayFormat: string = 'dd/MM/yyyy';
  @Input() inputOutputFormat: string = 'yyyy-MM-dd';

  @Input() presentation: PresentationTypeEnum = PresentationTypeEnum.Date;

  @Input() cancelText: string = 'COMMON.CANCEL';
  @Input() doneText: string = 'COMMON.OK';

  @Input() helperText?: string;
  @Input() isIncorrectValue: boolean = false;

  @Input() min: string = '';
  @Input() max: string = format(add(new Date(), {years: 10}), 'yyyy-MM-dd');
  @Input() allowedDates?: string[];

  @Input() lines: string = 'none';

  @Input() disabled: boolean = false;
  @Input() minuteValues: string | undefined;
  @Input() canDelete: boolean = false;

  @Input() set value(newValue: string) {
    this.internalValue = newValue;
    this.valueChange.emit(this.internalValue);

    if (newValue) {
      this.formattedDate = this.toString(this.toDate(this.value, this.inputOutputFormat), this.displayFormat);
    }
  }

  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();

  get value(): string {
    return this.internalValue;
  }

  constructor(
    protected modalCtrl: ModalController,
    protected animationsCtrl: AnimationsManagerService
  ) {
  }


  /**
   * Borrar fecha
   *
   */
  removeDate() {
    this.value = '';
    this.formattedDate = '';
    this.internalValue = '';
  }


  /**
   * Abrir modal para seleccionar la fecha
   *
   */
  public async openModal() {
    const modal = await this.modalCtrl.create({
      component: YuDatetimeModal,
      enterAnimation: this.animationsCtrl.showModalAnimation,
      leaveAnimation: this.animationsCtrl.hideModalAnimation,
      componentProps: {
        dateValue: this.value
          ? this.toString(this.toDate(this.value, this.inputOutputFormat), this.inputOutputFormat)
          : this.toString(new Date(), this.inputOutputFormat),
        presentation: this.presentation,
        cancelText: this.cancelText,
        doneText: this.doneText,
        min: this.min,
        max: this.max,
        allowedDates: this.allowedDates,
        inputOutputFormat: this.inputOutputFormat,
        minuteValues: this.minuteValues,
      },
      cssClass: 'transparent-modal force-fullscreen',
    });

    modal.onDidDismiss().then((data) => {
      if (data?.data) {
        this.value = this.toString(this.toDate(data.data, this.inputOutputFormat), this.inputOutputFormat);
      }
    });

    await modal.present();
  }


  /**
   * Convierte el texto en fecha
   *
   */
  private toDate(date: string, formatValue: string = 'yyyy-MM-dd'): Date {
    return parse(date, formatValue, new Date());
  }


  /**
   * Convierte la fecha en texto
   *
   */
  private toString(date: Date, formatValue: string = 'yyyy-MM-dd'): string {
    return format(date, formatValue, {locale: es});
  }
}

@Component({
  templateUrl: './yu-datetime-modal.html',
  standalone: false
})
export class YuDatetimeModal {

  @Input() dateValue!: string;
  @Input() presentation: PresentationTypeEnum = PresentationTypeEnum.Date;
  @Input() cancelText: string = 'Cancelar';
  @Input() doneText: string = 'Ok';
  @Input() min: string = '';
  @Input() max: string = '';
  @Input() allowedDates?: string[];
  @Input() dateDisabled: boolean = false;
  @Input() inputOutputFormat: string = 'yyyy-MM-dd';
  @Input() minuteValues: string | undefined = undefined;

  constructor(
    protected modalCtrl: ModalController
  ) {
  }


  /**
   * Asignar a la variable dateValue la fecha
   *
   */
  public changeDate(value: any) {

    if (!value) {
      return;
    }

    if (this.presentation === PresentationTypeEnum.Time) {
      this.dateValue = value;

    } else {
      this.dateValue = this.toString(parseISO(value));
    }
  }


  /**
   * Comprobar si la fecha está permitida
   *
   */
  public isDateAllowed = (date: string): boolean => {
    return !this.allowedDates || this.allowedDates.includes(date);
  };


  /**
   * Cancelar la fecha
   *
   */
  public async cancel(datetime: IonDatetime) {
    await datetime.cancel();
    this.dismiss();
  }


  /**
   * Aceptar la fecha
   *
   */
  public async done(datetime: IonDatetime) {
    await datetime.confirm();
    this.dismiss(this.dateValue);
  }


  /**
   * Convierte la fecha en texto
   *
   */
  private toString(date: Date): string {
    return format(date, this.inputOutputFormat);
  }


  /**
   * Cerrar modal
   *
   */
  private dismiss(value?: string) {
    this.modalCtrl.dismiss(value);
  }

}
